const PAGINATION_ELLIPSES = '...';

/**
 * Get all page numbers when the total page count is less than or equal to the maximum pages to show.
 * @param pageCount - Total number of pages.
 * @returns An array of all page numbers.
 */
const getAllPages = (pageCount: number): number[] => {
	const pages: number[] = [];
	for (let i = 0; i < pageCount; i++) {
		pages.push(i);
	}
	return pages;
};

/**
 * Get the page numbers to display in the pagination component.
 * @param pageCount - Total number of pages.
 * @param currentPage - The current page index.
 * @returns An array of page numbers and ellipses.
 */
const getPageNumbers = (pageCount: number, currentPage: number): (number | string)[] => {
	const maxPagesToShow = 5;
	const halfMaxPagesToShow = Math.floor(maxPagesToShow / 2);

	if (pageCount <= maxPagesToShow) {
		return getAllPages(pageCount);
	} else if (currentPage <= halfMaxPagesToShow) {
		return getStartPages(maxPagesToShow, pageCount);
	} else if (currentPage >= pageCount - halfMaxPagesToShow - 1) {
		return getEndPages(maxPagesToShow, pageCount);
	} else {
		return getMiddlePages(currentPage, halfMaxPagesToShow, pageCount);
	}
};

/**
 * Get the start pages and ellipses when the current page is near the beginning.
 * @param maxPagesToShow - Maximum number of pages to show.
 * @param pageCount - Total number of pages.
 * @returns An array of start pages and ellipses.
 */
const getStartPages = (maxPagesToShow: number, pageCount: number): (number | string)[] => {
	const pages: (number | string)[] = [];
	for (let i = 0; i < maxPagesToShow - 1; i++) {
		pages.push(i);
	}
	pages.push(PAGINATION_ELLIPSES);
	pages.push(pageCount - 1);
	return pages;
};

/**
 * Get the end pages and ellipses when the current page is near the end.
 * @param maxPagesToShow - Maximum number of pages to show.
 * @param pageCount - Total number of pages.
 * @returns An array of end pages and ellipses.
 */
const getEndPages = (maxPagesToShow: number, pageCount: number): (number | string)[] => {
	const pages: (number | string)[] = [];
	pages.push(0);
	pages.push(PAGINATION_ELLIPSES);
	for (let i = pageCount - (maxPagesToShow - 1); i < pageCount; i++) {
		pages.push(i);
	}
	return pages;
};

/**
 * Get the middle pages and ellipses when the current page is in the middle.
 * @param currentPage - The current page index.
 * @param halfMaxPagesToShow - Half of the maximum number of pages to show.
 * @param pageCount - Total number of pages.
 * @returns An array of middle pages and ellipses.
 */
const getMiddlePages = (currentPage: number, halfMaxPagesToShow: number, pageCount: number): (number | string)[] => {
	const pages: (number | string)[] = [];
	pages.push(0);
	pages.push(PAGINATION_ELLIPSES);
	for (let i = currentPage - halfMaxPagesToShow + 1; i <= currentPage + halfMaxPagesToShow - 1; i++) {
		pages.push(i);
	}
	pages.push(PAGINATION_ELLIPSES);
	pages.push(pageCount - 1);
	return pages;
};

export { getPageNumbers, getAllPages, getStartPages, getEndPages, getMiddlePages, PAGINATION_ELLIPSES };
